/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import config from './config';
import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */

export const b2cPolicies = {
    names: {
        signUpSignIn: "B2C_1_mv7mfa2"
        // ,
        // forgotPassword: "b2c_1_reset",
        // editProfile: "b2c_1_edit_profile"
    },
    authorities: {
        signUpSignIn: {
            authority: "https://ipsauth.b2clogin.com/ipsauth.onmicrosoft.com/B2C_1_mv7mfa2",
        }
        // ,
        // forgotPassword: {
        //     authority: "https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/b2c_1_reset",
        // },
        // editProfile: {
        //     authority: "https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/b2c_1_edit_profile"
        // }
    },
    authorityDomain: "ipsauth.b2clogin.com"
}


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: "5a794e7c-6070-4f8f-8a98-6145aec17b2e", // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: "/loginredirect", // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
        postLogoutRedirectUri: "/logout/", // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {	
        loggerOptions: {	
            loggerCallback: (level, message, containsPii) => {	
                if (containsPii) {		
                    return;		
                }		
                switch (level) {		
                    case LogLevel.Error:		
                        console.error(message);		
                        return;		
                    case LogLevel.Info:		
                        //console.info(message);		
                        return;		
                    case LogLevel.Verbose:		
                        console.debug(message);		
                        return;		
                    case LogLevel.Warning:		
                        console.warn(message);		
                        return;	
                    default:
                        console.error(message);
                        return;	
                }	
            }	
        }	
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["https://ipsauth.onmicrosoft.com/5a794e7c-6070-4f8f-8a98-6145aec17b2e/read"]
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// const silentRequest = {
//   scopes: ["openid", "profile"],
//   loginHint: "example@domain.net"
// };


/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
const scopes = ["https://ipsauth.onmicrosoft.com/5a794e7c-6070-4f8f-8a98-6145aec17b2e/read"];

export const protectedResources = {

    alarms: {
        endpoint: config.endpoints.alarms,
        scopes: scopes
    },
    alarm: {
        endpoint: config.endpoints.alarm,
        scopes: scopes
    },
    chartdata: {
        endpoint: config.endpoints.chartdata,
        scopes: scopes
    },
    datachanneldata: {
        endpoint: config.endpoints.datachanneldata,
        scopes: scopes
    },
    meterdata: {
        endpoint: config.endpoints.meterdata,
        scopes: scopes
    },
    alarmfilters: {
        endpoint: config.endpoints.alarmfilters,
        scopes: scopes
    },
    alarmupdatefilters: {
        endpoint: config.endpoints.alarmupdatefilters,
        scopes: scopes
    },
    alarmupdatestatus: {
        endpoint: config.endpoints.alarmupdatestatus,
        scopes: scopes
    },
    alarmstatushistory: {
        endpoint: config.endpoints.alarmstatushistory,
        scopes: scopes
    },
    alarmdataalarms: {
        endpoint: config.endpoints.alarmdataalarms,
        scopes: scopes
    },
    metertree: {
        endpoint: config.endpoints.metertree,
        scopes: scopes
    },
    meterdetails: {
        endpoint: config.endpoints.meterdetails,
        scopes: scopes
    },
    meterupdatedetails: {
        endpoint: config.endpoints.meterupdatedetails,
        scopes: scopes
    },
    metergroupdetails: {
        endpoint: config.endpoints.metergroupdetails,
        scopes: scopes
    },
    metersitedetails: {
        endpoint: config.endpoints.metersitedetails,
        scopes: scopes
    },
    updatemetersitedetails: {
        endpoint: config.endpoints.updatemetersitedetails,
        scopes: scopes
    },    
    useraccount: {
        endpoint: config.endpoints.useraccount,
        scopes: scopes
    },
    useraccountsetorganisation: {
        endpoint: config.endpoints.useraccountsetorganisation,
        scopes: scopes
    },
    datasources: {
        endpoint: config.endpoints.datasources,
        scopes: scopes
    },
    metercategories: {
        endpoint: config.endpoints.metercategories,
        scopes: scopes
    },
    metertreenodecategories: {
        endpoint: config.endpoints.metertreenodecategories,
        scopes: scopes
    },
    addmetercategory: {
        endpoint: config.endpoints.addmetercategory,
        scopes: scopes
    },
    deletemetercategory: {
        endpoint: config.endpoints.deletemetercategory,
        scopes: scopes
    },
    addexpecteddata: {
        endpoint: config.endpoints.addexpecteddata,
        scopes: scopes
    },
    deleteexpecteddata: {
        endpoint: config.endpoints.deleteexpecteddata,
        scopes: scopes
    },
    datatypes: {
        endpoint: config.endpoints.datatypes,
        scopes: scopes
    },
    organisations: {
        endpoint: config.endpoints.organisations,
        scopes: scopes
    },
    addmeter: {
        endpoint: config.endpoints.addmeter,
        scopes: scopes
    },
    addmetergroup: {
        endpoint: config.endpoints.addmetergroup,
        scopes: scopes
    },
    addmetersite: {
        endpoint: config.endpoints.addmetersite,
        scopes: scopes
    },
    datasourcesupload: {
        endpoint: config.endpoints.datasourcesupload,
        scopes: scopes
    },

    
}

